/**
 * User-initiated logout orchestration (API-4797).
 *
 * Only wired into `SettingsPage.handleLogout()` — the session-death paths
 * (`SessionExpiredPage`, `useTokenRefresh`'s `7punches:destroy` handler,
 * `tokenRefresh`'s `onSessionExpired`) keep their existing local-only
 * teardown, since the session/token is already invalid there.
 *
 * Local React state (`resetClient`/`clearAuth`/`clearAccount`) stays owned by
 * the calling component — this helper only handles the network-facing side
 * effects: best-effort revoke, then (browser mode only) redirecting the
 * browser to FusionAuth to clear the SSO cookie.
 */

import { env } from '@src/config/env';
import { revokeRefreshTokens } from '@src/api/authRevokeApi';
import type { AuthMode } from './AuthContext';
import { buildLogoutUrl } from './authCodeFlow';

export interface LogoutParams {
    mode: AuthMode;
    accessToken: string | null;
    refreshToken: string | null;
}

/**
 * Best-effort revoke of the current session's refresh token (and the
 * FusionAuth SSO session token). Never throws — a network/HTTP failure must
 * never block local logout.
 */
async function revokeBestEffort(
    accessToken: string,
    refreshToken: string | null
): Promise<void> {
    if (!refreshToken) return;
    try {
        await revokeRefreshTokens({
            applicationId: env.oauthClientId,
            accessToken,
            refreshToken,
            apiBaseUrl: env.apiBaseUrl
        });
    } catch {
        // Best-effort — swallow network/HTTP failures so logout always proceeds.
    }
}

/**
 * Revoke the current session's refresh token (best-effort) and, in browser
 * mode, redirect to FusionAuth's `/oauth2/logout` to clear the shared SSO
 * cookie. In webview mode, no redirect happens — the host owns the web
 * context, and revoke alone is sufficient.
 *
 * Callers are responsible for clearing local state (`resetClient`,
 * `clearAuth`, `clearAccount`) and, in webview mode, navigating to `/login`
 * after this resolves.
 */
export async function logout(params: LogoutParams): Promise<void> {
    if (params.accessToken) {
        await revokeBestEffort(params.accessToken, params.refreshToken);
    }

    if (params.mode === 'browser') {
        window.location.assign(
            buildLogoutUrl({
                clientId: env.oauthClientId,
                postLogoutRedirectUri: `${window.location.origin}/login`,
                authority: env.oauthAuthority
            })
        );
    }
}
